import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../components/Common/Breadcrumb'
import HelpModal from '../../components/HelpModal/HelpModal'
import timesheetService from '../../services/timesheetService'
import projectService from '../../services/projectService'
import employeeMasterService from '../../services/employeeMasterService'
import taskService from '../../services/taskService'
import autoIncrementService from '../../services/autoIncrementService'
import { 
  FileText,
  Calendar,
  DollarSign,
  AlertCircle,
  HelpCircle
} from 'lucide-react'

const TimesheetForm = ({ mode = 'create', timesheetId = null, initialData = null, breadcrumbItems: customBreadcrumbItems = null }) => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [timesheetDataLoading, setTimesheetDataLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [masterDataLoaded, setMasterDataLoaded] = useState(false)
  const [isLoadingTimesheetNumber, setIsLoadingTimesheetNumber] = useState(false)
  const isEditMode = mode === 'edit'
  const isViewMode = mode === 'view'
  const isReadOnly = isViewMode

  const [masterData, setMasterData] = useState({
    projects: [],
    employees: [],
    tasks: []
  })

  const [formData, setFormData] = useState({
    timesheetNumber: '',
    employee: '',
    project: '',
    task: '',
    entryDate: new Date().toISOString().split('T')[0],
    hours: 0,
    minutes: 0,
    activityType: 'Development',
    description: '',
    isBillable: true,
    billingRate: 0,
    currency: 'INR',
    status: 'Draft',
    location: 'Office',
    notes: ''
  })

  const [errors, setErrors] = useState({})

  // Load master data on component mount
  useEffect(() => {
    if (!masterDataLoaded && !loading) {
      loadMasterData()
    }
  }, [masterDataLoaded, loading])

  // Fetch next timesheet number (only for new timesheets)
  useEffect(() => {
    if (!isEditMode && !isViewMode && masterDataLoaded && !isLoadingTimesheetNumber && formData.timesheetNumber === '') {
      fetchNextTimesheetNumber()
    }
  }, [isEditMode, isViewMode, masterDataLoaded, isLoadingTimesheetNumber, formData.timesheetNumber])

  // Load timesheet data for edit/view mode - wait for master data to be loaded first
  useEffect(() => {
    if ((isEditMode || isViewMode) && timesheetId && masterDataLoaded) {
      if (initialData) {
        console.log('Using initialData:', initialData)
        populateFormData(initialData)
      } else if (!timesheetDataLoading) {
        console.log('Loading timesheet data for ID:', timesheetId)
        loadTimesheetData()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheetId, isEditMode, isViewMode, masterDataLoaded, timesheetDataLoading])

  const loadTimesheetData = async () => {
    try {
      setTimesheetDataLoading(true)
      const response = await timesheetService.getTimesheetById(timesheetId)
      
      if (response.success && response.data) {
        console.log('Timesheet data loaded:', response.data)
        populateFormData(response.data)
      } else {
        toast.error(response.message || 'Failed to load timesheet')
        navigate('/timesheets/list')
      }
    } catch (error) {
      console.error('Error loading timesheet:', error)
      toast.error('Failed to load timesheet')
      navigate('/timesheets/list')
    } finally {
      setTimesheetDataLoading(false)
    }
  }

  const populateFormData = (data) => {
    console.log('Populating form data:', data)
    
    // Extract project ID
    let projectValue = ''
    if (data.project) {
      if (typeof data.project === 'object' && data.project !== null) {
        projectValue = String(data.project._id || data.project.id || '')
      } else {
        projectValue = String(data.project)
      }
    }

    // Extract task ID
    let taskValue = ''
    if (data.task) {
      if (typeof data.task === 'object' && data.task !== null) {
        taskValue = String(data.task._id || data.task.id || '')
      } else {
        taskValue = String(data.task)
      }
    }

    // Extract employee ID
    let employeeValue = ''
    if (data.employee) {
      if (typeof data.employee === 'object' && data.employee !== null) {
        employeeValue = String(data.employee._id || data.employee.id || '')
      } else {
        employeeValue = String(data.employee)
      }
    }

    // Calculate hours and minutes from totalMinutes
    const totalMinutes = data.totalMinutes || 0
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    setFormData({
      timesheetNumber: data.timesheetNumber || '',
      employee: employeeValue,
      project: projectValue,
      task: taskValue,
      entryDate: data.entryDate ? new Date(data.entryDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      hours: data.hours !== undefined ? data.hours : hours,
      minutes: data.minutes !== undefined ? data.minutes : minutes,
      activityType: data.activityType || 'Development',
      description: data.description || '',
      isBillable: data.isBillable !== undefined ? data.isBillable : true,
      billingRate: data.billingRate || 0,
      currency: data.currency || 'INR',
      status: data.status || 'Draft',
      location: data.location || 'Office',
      notes: data.notes || ''
    })
    setErrors({})
  }

  const fetchNextTimesheetNumber = async () => {
    try {
      setIsLoadingTimesheetNumber(true)
      const response = await autoIncrementService.getCurrentValue('TIMESHEET')

      if (response.success && response.data && response.data.formattedValue) {
        setFormData(prev => ({
          ...prev,
          timesheetNumber: response.data.formattedValue
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          timesheetNumber: 'TS-000001'
        }))
      }
    } catch (error) {
      console.error('Error fetching next timesheet number:', error)
      setFormData(prev => ({
        ...prev,
        timesheetNumber: 'TS-000001'
      }))
    } finally {
      setIsLoadingTimesheetNumber(false)
    }
  }

  const loadMasterData = async () => {
    try {
      setLoading(true)
      const [
        projectsRes,
        employeesRes,
        tasksRes
      ] = await Promise.allSettled([
        projectService.getAllProjects({ limit: 1000, status: 'Active' }),
        employeeMasterService.getAllEmployees({ limit: 1000, status: 'Active' }),
        taskService.getAllTasks({ limit: 1000 })
      ])

      const getData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
          if (res.projects) {
            return res.projects
          }
          if (res.tasks) {
            return res.tasks
          }
        }
        return []
      }

      const getEmployeesData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
        }
        return []
      }

      setMasterData({
        projects: getData(projectsRes),
        employees: getEmployeesData(employeesRes),
        tasks: getData(tasksRes)
      })

      const failedRequests = [
        { name: 'Projects', result: projectsRes },
        { name: 'Employees', result: employeesRes },
        { name: 'Tasks', result: tasksRes }
      ].filter(item => item.result.status === 'rejected')

      if (failedRequests.length > 0) {
        console.warn('Some master data failed to load:', failedRequests.map(r => r.name))
        // Check if errors were already handled by interceptor
        const hasNetworkError = failedRequests.some(r => {
          const error = r.result.reason
          return error && (error._errorHandled || error.request)
        })
        if (!hasNetworkError) {
          if (failedRequests.length === 3) {
            toast.error('Unable to connect to backend server. Please ensure the server is running.')
          } else {
            toast.warning(`Failed to load some data: ${failedRequests.map(r => r.name).join(', ')}`)
          }
        }
      } else {
        console.log('All master data loaded successfully')
      }
      
      setMasterDataLoaded(true)
    } catch (error) {
      console.error('Error loading master data:', error)
      if (!error._errorHandled && !error.request) {
        toast.error('Failed to load master data. Please check your connection.')
      }
      setMasterDataLoaded(true)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  // Filter tasks by selected project
  const getFilteredTasks = () => {
    if (!formData.project) {
      return []
    }
    return masterData.tasks.filter(task => {
      const taskProjectId = task.project?._id || task.project || ''
      return String(taskProjectId) === String(formData.project)
    })
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.employee) {
      newErrors.employee = 'Employee is required'
    }

    if (!formData.project) {
      newErrors.project = 'Project is required'
    }

    if (!formData.entryDate) {
      newErrors.entryDate = 'Entry date is required'
    }

    if (formData.hours < 0 || formData.hours > 24) {
      newErrors.hours = 'Hours must be between 0 and 24'
    }

    if (formData.minutes < 0 || formData.minutes > 59) {
      newErrors.minutes = 'Minutes must be between 0 and 59'
    }

    if (formData.hours === 0 && formData.minutes === 0) {
      newErrors.hours = 'At least one hour or minute must be entered'
    }

    if (formData.isBillable && formData.billingRate < 0) {
      newErrors.billingRate = 'Billing rate cannot be negative'
    }

    setErrors(newErrors)
    
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0]
      const errorElement = document.getElementById(firstErrorField) || document.querySelector(`[name="${firstErrorField}"]`)
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        errorElement.focus()
      }
    }
    
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      const errorCount = Object.keys(errors).length
      const errorFields = Object.keys(errors).map(field => {
        const fieldNames = {
          employee: 'Employee',
          project: 'Project',
          entryDate: 'Entry Date',
          hours: 'Hours',
          minutes: 'Minutes',
          billingRate: 'Billing Rate'
        }
        return fieldNames[field] || field
      }).join(', ')
      toast.error(`Please fix ${errorCount} error(s): ${errorFields}`)
      return
    }

    try {
      setLoading(true)
      let response
      if (isEditMode) {
        response = await timesheetService.updateTimesheet(timesheetId, formData)
        if (response.success) {
          toast.success('Timesheet updated successfully')
          navigate('/timesheets/list')
        } else {
          toast.error(response.message || 'Failed to update timesheet')
        }
      } else {
        // Remove timesheetNumber when creating - backend will auto-generate it via pre-save hook
        const { timesheetNumber, ...createData } = formData
        response = await timesheetService.createTimesheet(createData)
        if (response.success) {
          toast.success('Timesheet created successfully')
          navigate('/timesheets/list')
        } else {
          toast.error(response.message || 'Failed to create timesheet')
        }
      }
    } catch (error) {
      console.error('Error saving timesheet:', error)
      toast.error(error.response?.data?.message || 'Failed to save timesheet')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData(prev => ({
      ...initialData || {
        timesheetNumber: prev.timesheetNumber,
        employee: '',
        project: '',
        task: '',
        entryDate: new Date().toISOString().split('T')[0],
        hours: 0,
        minutes: 0,
        activityType: 'Development',
        description: '',
        isBillable: true,
        billingRate: 0,
        currency: 'INR',
        status: 'Draft',
        location: 'Office',
        notes: ''
      }
    }))
    setErrors({})
  }

  const handleTestData = () => {
    const today = new Date()
    setFormData(prev => ({
      ...prev,
      employee: masterData.employees.length > 0 ? (masterData.employees[0]._id || masterData.employees[0].id) : '',
      project: masterData.projects.length > 0 ? (masterData.projects[0]._id || masterData.projects[0].id) : '',
      task: getFilteredTasks().length > 0 ? (getFilteredTasks()[0]._id || getFilteredTasks()[0].id) : '',
      entryDate: today.toISOString().split('T')[0],
      hours: 8,
      minutes: 0,
      activityType: 'Development',
      description: 'Working on feature development and code review',
      isBillable: true,
      billingRate: 1500,
      currency: 'INR',
      status: 'Draft',
      location: 'Office',
      notes: 'Test timesheet entry'
    }))
    setErrors({})
  }

  // Listen for F1 key to populate test data (only in create mode)
  useEffect(() => {
    if (isEditMode || isViewMode) return // Don't add listener in edit/view mode

    const handleKeyPress = (event) => {
      // Check if F1 key is pressed (keyCode 112 or key === 'F1')
      if (event.key === 'F1' || event.keyCode === 112) {
        event.preventDefault() // Prevent browser help menu
        handleTestData()
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyPress)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [isEditMode, isViewMode, masterData]) // Include dependencies used by handleTestData

  const breadcrumbItems = customBreadcrumbItems || [
    { label: 'Timesheets', path: '/timesheets', onClick: () => navigate('/timesheets') },
    { label: 'Timesheet List', path: '/timesheets/list', onClick: () => navigate('/timesheets/list') },
    { label: isEditMode ? 'Edit Timesheet' : isViewMode ? 'View Timesheet' : 'Create New', path: isEditMode ? `/timesheets/edit/${timesheetId}` : isViewMode ? `/timesheets/view/${timesheetId}` : '/timesheets/new', isCurrent: true }
  ]

  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: FileText },
    { id: 'billing', label: 'Billing & Status', icon: DollarSign }
  ]

  const helpContent = (
    <>
      <h3>What is Timesheet Entry?</h3>
      <p>Timesheet Entry lets you <strong>create and manage time tracking and logging</strong> for projects and tasks. You log hours against an employee, project, and optionally task, with entry date, activity type, description, and optional billing.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Information</strong> – Timesheet number, employee, project, task, entry date, hours, minutes, activity type, description, location, notes.</p>
      <p><strong>Billing &amp; Status</strong> – Billable flag, billing rate, currency, status (Draft/Submitted/Approved).</p>
      <h3>How to use</h3>
      <p>Select <strong>employee</strong> and <strong>project</strong> (and optionally task), enter date and hours/minutes. Use <strong>Create Timesheet</strong> to save. Submit for approval from the list when required. Access via Projects → Transactions (Timesheets) or main menu Timesheets.</p>
    </>
  )

  if (loading || timesheetDataLoading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading {timesheetDataLoading ? 'timesheet' : 'master'} data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Timesheet Entry</h1>
          <p>Create and manage timesheet information</p>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Timesheet Entry</h2>
        </div>

        <div className="tab-navigation">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                type="button"
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            )
          })}
        </div>

        <form className="form-content" onSubmit={handleSubmit}>
          {/* Validation Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div style={{
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #dc2626',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <AlertCircle size={16} style={{ color: '#DC2626' }} />
                <strong style={{ color: '#DC2626' }}>Please fix the following errors:</strong>
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#DC2626' }}>
                {Object.entries(errors).map(([field, message]) => {
                  const fieldNames = {
                    employee: 'Employee',
                    project: 'Project',
                    entryDate: 'Entry Date',
                    hours: 'Hours',
                    minutes: 'Minutes',
                    billingRate: 'Billing Rate'
                  }
                  return (
                    <li key={field} style={{ marginBottom: '4px', cursor: 'pointer' }}
                        onClick={() => {
                          const element = document.getElementById(field) || document.querySelector(`[name="${field}"]`)
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                            element.focus()
                          }
                        }}>
                      <strong>{fieldNames[field] || field}:</strong> {message}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'basic' && (
              <div>
                <div className="section-header">
                  <h3>Basic Information</h3>
                </div>

                {/* Row 1: Timesheet Number, Employee, Project */}
                <div className="grid-row-3">
                  <div className="form-group">
                    <label htmlFor="timesheetNumber" className="form-label">
                      Timesheet Number
                    </label>
                    <input
                      id="timesheetNumber"
                      type="text"
                      value={isLoadingTimesheetNumber ? 'Loading...' : formData.timesheetNumber}
                      className="form-input"
                      placeholder="Auto-generated"
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="employee" className="form-label required">
                      Employee
                    </label>
                    <select
                      id="employee"
                      value={formData.employee}
                      onChange={(e) => handleInputChange('employee', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Employee</option>
                      {masterData.employees.length > 0 ? (
                        masterData.employees.map((employee) => {
                          const employeeId = employee._id || employee.id
                          return (
                            <option key={employeeId} value={String(employeeId)}>
                              {employee.employeeName} {employee.designation ? `- ${employee.designation}` : ''}
                            </option>
                          )
                        })
                      ) : (
                        <option value="" disabled>No employees available</option>
                      )}
                    </select>
                    {errors.employee && (
                      <p className="form-error">{errors.employee}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="project" className="form-label required">
                      Project
                    </label>
                    <select
                      id="project"
                      value={formData.project}
                      onChange={(e) => {
                        handleInputChange('project', e.target.value)
                        // Clear task when project changes
                        handleInputChange('task', '')
                      }}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Project</option>
                      {masterData.projects.length > 0 ? (
                        masterData.projects.map((project) => {
                          const projectId = project._id || project.id
                          return (
                            <option key={projectId} value={String(projectId)}>
                              {project.projectNumber} - {project.projectName}
                            </option>
                          )
                        })
                      ) : (
                        <option value="" disabled>No projects available</option>
                      )}
                    </select>
                    {errors.project && (
                      <p className="form-error">{errors.project}</p>
                    )}
                  </div>
                </div>

                {/* Row 2: Task, Entry Date, Activity Type */}
                <div className="grid-row-3">
                  <div className="form-group">
                    <label htmlFor="task" className="form-label">
                      Task
                    </label>
                    <select
                      id="task"
                      value={formData.task}
                      onChange={(e) => handleInputChange('task', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly || !formData.project}
                      style={isReadOnly || !formData.project ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Task</option>
                      {getFilteredTasks().length > 0 ? (
                        getFilteredTasks().map((task) => {
                          const taskId = task._id || task.id
                          return (
                            <option key={taskId} value={String(taskId)}>
                              {task.taskNumber} - {task.taskName}
                            </option>
                          )
                        })
                      ) : (
                        <option value="" disabled>No tasks available for selected project</option>
                      )}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="entryDate" className="form-label required">
                      Entry Date
                    </label>
                    <input
                      id="entryDate"
                      type="date"
                      value={formData.entryDate}
                      onChange={(e) => handleInputChange('entryDate', e.target.value)}
                      className="form-input"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.entryDate && (
                      <p className="form-error">{errors.entryDate}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="activityType" className="form-label">
                      Activity Type
                    </label>
                    <select
                      id="activityType"
                      value={formData.activityType}
                      onChange={(e) => handleInputChange('activityType', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Development">Development</option>
                      <option value="Testing">Testing</option>
                      <option value="Design">Design</option>
                      <option value="Meeting">Meeting</option>
                      <option value="Documentation">Documentation</option>
                      <option value="Support">Support</option>
                      <option value="Training">Training</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Row 3: Hours, Minutes, Location */}
                <div className="grid-row-3">
                  <div className="form-group">
                    <label htmlFor="hours" className="form-label required">
                      Hours
                    </label>
                    <input
                      id="hours"
                      type="number"
                      value={formData.hours}
                      onChange={(e) => handleInputChange('hours', parseInt(e.target.value) || 0)}
                      className="form-input"
                      placeholder="0"
                      min="0"
                      max="24"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.hours && (
                      <p className="form-error">{errors.hours}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="minutes" className="form-label required">
                      Minutes
                    </label>
                    <input
                      id="minutes"
                      type="number"
                      value={formData.minutes}
                      onChange={(e) => handleInputChange('minutes', parseInt(e.target.value) || 0)}
                      className="form-input"
                      placeholder="0"
                      min="0"
                      max="59"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.minutes && (
                      <p className="form-error">{errors.minutes}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="location" className="form-label">
                      Location
                    </label>
                    <select
                      id="location"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Office">Office</option>
                      <option value="Remote">Remote</option>
                      <option value="Client Site">Client Site</option>
                      <option value="Travel">Travel</option>
                    </select>
                  </div>
                </div>

                {/* Row 4: Description */}
                <div className="grid-row-1">
                  <div className="form-group">
                    <label htmlFor="description" className="form-label">
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="form-input"
                      placeholder="Enter work description"
                      rows="3"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    ></textarea>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'billing' && (
              <div>
                <div className="section-header">
                  <h3>Billing & Status</h3>
                </div>

                {/* Row 1: Is Billable, Billing Rate, Currency */}
                <div className="grid-row-3">
                  <div className="form-group">
                    <label htmlFor="isBillable" className="form-label">
                      Is Billable
                    </label>
                    <select
                      id="isBillable"
                      value={formData.isBillable ? 'Yes' : 'No'}
                      onChange={(e) => handleInputChange('isBillable', e.target.value === 'Yes')}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="billingRate" className="form-label">
                      Billing Rate
                    </label>
                    <input
                      id="billingRate"
                      type="number"
                      value={formData.billingRate}
                      onChange={(e) => handleInputChange('billingRate', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      readOnly={isReadOnly || !formData.isBillable}
                      disabled={isReadOnly || !formData.isBillable}
                      style={isReadOnly || !formData.isBillable ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.billingRate && (
                      <p className="form-error">{errors.billingRate}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="currency" className="form-label">
                      Currency
                    </label>
                    <select
                      id="currency"
                      value={formData.currency}
                      onChange={(e) => handleInputChange('currency', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="CAD">CAD</option>
                      <option value="AUD">AUD</option>
                    </select>
                  </div>
                </div>

                {/* Row 2: Status, Notes */}
                <div className="grid-row-2">
                  <div className="form-group">
                    <label htmlFor="status" className="form-label">
                      Status
                    </label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Submitted">Submitted</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Invoiced">Invoiced</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="notes" className="form-label">
                      Notes
                    </label>
                    <input
                      id="notes"
                      type="text"
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      className="form-input"
                      placeholder="Additional notes"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          {isViewMode ? (
            <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secondary" onClick={() => navigate('/timesheets/list')}>
                Back
              </button>
            </div>
          ) : (
            <div className="form-actions" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                <button type="button" className="btn-secondary" onClick={handleReset}>
                  Reset
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? (
                    <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                  ) : (
                    <span>{isEditMode ? 'Update Timesheet' : 'Create Timesheet'}</span>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Timesheet Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default TimesheetForm

